import { useAdminAuth } from '../../hooks/useAdminAuth.js';
import { T, PageHeader, Scaffold, card } from './ui.jsx';
import AdminOverview from './AdminOverview.jsx';
import AdminLearners from './AdminLearners.jsx';
import AdminAnalytics from './AdminAnalytics.jsx';
import AdminMemory from './AdminMemory.jsx';
import AdminFinance from './AdminFinance.jsx';
import AdminSupport from './AdminSupport.jsx';
import AdminOutreach from './AdminOutreach.jsx';
import AdminSubmissions from './AdminSubmissions.jsx';

// Icon path set (single-path line icons).
const I = {
  overview: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  learners: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
  curriculum: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20 M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z',
  memory: 'M12 2a5 5 0 0 0-5 5c0 1 .3 1.9.8 2.6A4 4 0 0 0 6 17a4 4 0 0 0 6 3 4 4 0 0 0 6-3 4 4 0 0 0-1.8-7.4c.5-.7.8-1.6.8-2.6a5 5 0 0 0-5-5z',
  assessments: 'M9 11l3 3 8-8 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  analytics: 'M18 20V10 M12 20V4 M6 20v-6',
  outreach: 'M3 11l18-5v12L3 14v-3z M11.6 16.8a3 3 0 1 1-5.8-1.6',
  submissions: 'M22 12h-6l-2 3h-4l-2-3H2 M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
  finance: 'M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  support: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M4.9 4.9l4.2 4.2 M14.9 14.9l4.2 4.2 M14.9 9.1l4.2-4.2 M9.1 14.9l-4.2 4.2 M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  system: 'M22 12h-4l-3 9L9 3l-3 9H2',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-2.82 1.18V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 8.4 a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 2.82 1.18l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
};

const NAV = [
  { id: 'overview',    label: 'Overview',      path: '/admin' },
  { id: 'learners',    label: 'Learners',      path: '/admin/learners' },
  { id: 'curriculum',  label: 'Curriculum',    path: '/admin/curriculum' },
  { id: 'memory',      label: 'M[Emory]',      path: '/admin/memory' },
  { id: 'assessments', label: 'Assessments',   path: '/admin/assessments' },
  { id: 'analytics',   label: 'Analytics',     path: '/admin/analytics' },
  { id: 'outreach',    label: 'Outreach',      path: '/admin/outreach' },
  { id: 'submissions', label: 'Submissions',   path: '/admin/submissions' },
  { id: 'finance',     label: 'Finance',       path: '/admin/finance' },
  { id: 'support',     label: 'Support',       path: '/admin/support' },
  { id: 'system',      label: 'System Health', path: '/admin/system' },
  { id: 'settings',    label: 'Settings',      path: '/admin/settings' },
];

function NavIcon({ name }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d={I[name]} />
    </svg>
  );
}

function activeId() {
  if (typeof window === 'undefined') return 'overview';
  const p = window.location.pathname.replace(/\/$/, '').toLowerCase();
  const hit = NAV.find(n => n.path === p);
  return hit ? hit.id : 'overview';
}

function Section({ id }) {
  switch (id) {
    case 'overview':   return <AdminOverview />;
    case 'learners':   return <AdminLearners />;
    case 'analytics':  return <AdminAnalytics />;
    case 'memory':     return <AdminMemory />;
    case 'finance':    return <AdminFinance />;
    case 'support':    return <AdminSupport />;
    case 'curriculum': return (
      <div><PageHeader title="Curriculum" subtitle="Tracks, modules, and lesson coverage." />
        <Scaffold title="Curriculum management" body="Track/module/lesson editing and coverage stats land here. Lesson-level progress needs an admin read policy on lesson_progress (see the unlock migration) before completion data can render." />
      </div>);
    case 'assessments': return (
      <div><PageHeader title="Assessments" subtitle="Checkpoint and Fast-Track performance." />
        <Scaffold title="Assessment analytics" body="Pass rates, attempts-to-clear, and weak-question analysis. Needs an admin read policy on quiz_attempts / lesson_progress to populate." />
      </div>);
    case 'outreach':   return <AdminOutreach />;
    case 'submissions': return <AdminSubmissions />;
    case 'system': return (
      <div><PageHeader title="System Health" subtitle="Live service status." />
        <div style={{ ...card }}>
          {[['Supabase', 'live'], ['OpenAI API', 'live'], ['Adzuna feed', 'live']].map(([n, s]) => (
            <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: T.emerald, boxShadow: '0 0 0 3px rgba(5,150,105,0.18)' }} />
              <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{n}</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: T.emerald }}>{s}</span>
            </div>
          ))}
          <p style={{ fontSize: 11.5, color: T.slate, margin: '12px 0 0' }}>Static for now — real health checks (ping/latency) are a later pass.</p>
        </div>
      </div>);
    case 'settings': return (
      <div><PageHeader title="Settings" subtitle="Admin console preferences." />
        <Scaffold title="Console settings" body="Admin roster, alert thresholds, and export defaults will live here." />
      </div>);
    default: return <AdminOverview />;
  }
}

export default function AdminShell() {
  const status = useAdminAuth();
  if (status !== 'admin') return null; // checking → nothing; denied → redirecting
  const active = activeId();

  return (
    <div style={{ height: '100%', display: 'flex', background: T.pageBg, fontFamily: "'Inter','Segoe UI',sans-serif", overflow: 'hidden' }}>
      {/* Sidebar */}
      <div style={{ width: 222, flexShrink: 0, height: '100%', background: T.sidebar, borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <img src="/logo.jpg" alt="AdaptOS" style={{ height: 40, width: 'auto', maxWidth: '100%', objectFit: 'contain', borderRadius: 8, display: 'block' }} />
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: 'rgba(255,255,255,0.55)', margin: '8px 0 0', textTransform: 'uppercase' }}>Admin Console</p>
        </div>
        <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map(n => {
            const on = active === n.id;
            return (
              <a key={n.id} href={n.path} style={{
                display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderRadius: 8, textDecoration: 'none',
                fontSize: 13, fontWeight: on ? 800 : 600,
                color: on ? '#fff' : 'rgba(255,255,255,0.62)',
                background: on ? 'rgba(217,119,6,0.14)' : 'transparent',
                borderLeft: `3px solid ${on ? T.amber : 'transparent'}`,
              }}>
                <NavIcon name={n.id} /> {n.label}
              </a>
            );
          })}
        </nav>
        <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>A</span>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>ajaye@adaptos.app</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: 0 }}>Admin</p>
            </div>
          </div>
          <a href="/" style={{ display: 'block', textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', padding: '7px 0', borderRadius: 8, background: 'rgba(255,255,255,0.06)' }}>← Back to App</a>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto', padding: '28px 32px' }}>
        <div style={{ maxWidth: 1180 }}>
          <Section id={active} />
        </div>
      </div>
    </div>
  );
}
